import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  Backdrop,
  CircularProgress,
  Alert,
} from "@mui/material";
import { getApiErrorMessage } from "../utils/apiErrorMessage";
import { useNavigate, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import moment from "moment";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2";
import "select2/dist/css/select2.min.css";
import "./RaiseComplaint.css";
import { complaintAPI, masterDataAPI } from "../services/api";
import { renderSrLinkHtml, renderStatusHtml } from "../utils/xss";
import { useSidebar } from "../contexts/SidebarContext";
import { hasUserType, getUser } from "../utils/auth";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import {
  saveComplaintsListState,
  readComplaintsListState,
  clearComplaintsListState,
  readComplaintsListRestoreSnapshot,
  markComplaintsListRestored,
} from "../utils/complaintsListState";
import { markBcmServiceRequestListRestored } from "../utils/bcmServiceRequestListState";

const Complaints = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const loadSeqRef = useRef(0);
  const srTableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const dateRangePickerRef = useRef(null);
  const { sidebarOpen } = useSidebar();
  const currentUser = getUser();
  const createdByForList =
    currentUser?.userIdCode || currentUser?.UserIdCode || null;

  const readInvoiceFromSearchParams = (searchParams) => {
    const raw =
      searchParams?.get("invoiceNo") ||
      searchParams?.get("searchText") ||
      searchParams?.get("invoiceNumber") ||
      "";
    return raw ? decodeURIComponent(String(raw)).trim() : "";
  };

  /** Dashboard drill-down query params (customer/date already applied in sp_GetServiceRequestList). */
  const getUrlDrilldownParams = () => {
    const searchParams = new URLSearchParams(location.search);
    const customerCode = searchParams.get("customerCode");
    const fromDate = searchParams.get("fromDate");
    const toDate = searchParams.get("toDate");
    const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
    return {
      customerCode: customerCode ? decodeURIComponent(customerCode).trim() : "",
      fromDate: fromDate && isIso(fromDate) ? decodeURIComponent(fromDate).trim() : "",
      toDate: toDate && isIso(toDate) ? decodeURIComponent(toDate).trim() : "",
      invoiceNo: readInvoiceFromSearchParams(searchParams),
      serverFilteredByCustomer: Boolean(customerCode && String(customerCode).trim()),
      serverFilteredByDate:
        Boolean(fromDate && toDate && isIso(fromDate) && isIso(toDate)),
      serverFilteredByInvoice: Boolean(readInvoiceFromSearchParams(searchParams)),
    };
  };

  const getCurrentFinancialYearRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0 = Jan, 3 = Apr
    const fyStartYear = month < 3 ? year - 1 : year;
    const fyEndYear = month < 3 ? year : year + 1;
    const start = `${fyStartYear}-04-01`;
    const end = `${fyEndYear}-03-31`;
    const fmt = (y, m, d) => {
      const dd = String(d).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      return `${dd}-${mm}-${y}`;
    };
    const displayText = `${fmt(fyStartYear, 4, 1)} - ${fmt(fyEndYear, 3, 31)}`;
    return { start, end, displayText };
  };

  const defaultFY = getCurrentFinancialYearRange();
  const savedRestoreSnapshot = readComplaintsListRestoreSnapshot();
  const restoredStartDate = savedRestoreSnapshot?.startDate || defaultFY.start;
  const restoredEndDate = savedRestoreSnapshot?.endDate || defaultFY.end;
  const restoredDateRangeText =
    savedRestoreSnapshot?.startDate && savedRestoreSnapshot?.endDate
      ? `${moment(savedRestoreSnapshot.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(
          savedRestoreSnapshot.endDate,
          "YYYY-MM-DD"
        ).format("DD-MM-YYYY")}`
      : defaultFY.displayText;

  const invoiceSearchRef = useRef(
    savedRestoreSnapshot?.invoiceSearch != null
      ? String(savedRestoreSnapshot.invoiceSearch || "").trim()
      : ""
  );

  // Status options from Greenlam.SRSTATUSMASTER (loaded via API)
  const [statusOptions, setStatusOptions] = useState([]);

  // Filter states Ã¢â‚¬â€ default date range: current financial year (same as /csrp/bcm-service-requests)
  const [statusFilter, setStatusFilter] = useState(() =>
    Array.isArray(savedRestoreSnapshot?.statusFilter) ? [...savedRestoreSnapshot.statusFilter] : []
  );
  const [categoryFilter, setCategoryFilter] = useState(
    () => String(savedRestoreSnapshot?.categoryFilter || "")
  );
  const [customerFilter, setCustomerFilter] = useState(
    () => String(savedRestoreSnapshot?.customerFilter || "")
  );
  const [bvOptions, setBvOptions] = useState([]);
  const [bvFilter, setBvFilter] = useState(() =>
    Array.isArray(savedRestoreSnapshot?.bvFilter) ? [...savedRestoreSnapshot.bvFilter] : []
  );

  const STATUS_QUERY_MAP = {
    // Dashboard Open SR Count = Pending only (card + drill-down).
    "1": "Pending",
    // Dashboard "Rejected SR Count" uses status=4 but dropdown expects "Rejected".
    "4": "Rejected",
    // Dashboard "Closed SR Count" uses status=5 but dropdown expects "Closed".
    "5": "Closed",
    // Dashboard "Special Case SR Count" uses status=3 for "Reopened" selection.
    "3": "Reopened",
    // Dashboard "WIP" card uses status=7.
    "7": "WIP",
  };

  // Keep refs in sync so Filter click always reads the latest multiselect values
  // (avoids stale closure when user clicks Filter immediately after checking boxes).
  const statusFilterRef = useRef(
    Array.isArray(savedRestoreSnapshot?.statusFilter) ? [...savedRestoreSnapshot.statusFilter] : []
  );
  const bvFilterRef = useRef(
    Array.isArray(savedRestoreSnapshot?.bvFilter) ? [...savedRestoreSnapshot.bvFilter] : []
  );
  const categoryFilterRef = useRef(String(savedRestoreSnapshot?.categoryFilter || ""));
  const customerFilterRef = useRef(String(savedRestoreSnapshot?.customerFilter || ""));
  const startDateRef = useRef(restoredStartDate);
  const endDateRef = useRef(restoredEndDate);
  const listLocationRef = useRef({ pathname: "/complaints", search: "" });
  const restoreAppliedRef = useRef(Boolean(savedRestoreSnapshot));
  const [startDate, setStartDate] = useState(restoredStartDate);
  const [endDate, setEndDate] = useState(restoredEndDate);
  const [dateRangeText, setDateRangeText] = useState(restoredDateRangeText);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(() => {
    if (!savedRestoreSnapshot) return true;
    return !(
      (Array.isArray(savedRestoreSnapshot.statusFilter) &&
        savedRestoreSnapshot.statusFilter.length > 0) ||
      (Array.isArray(savedRestoreSnapshot.bvFilter) &&
        savedRestoreSnapshot.bvFilter.length > 0) ||
      savedRestoreSnapshot.categoryFilter ||
      savedRestoreSnapshot.customerFilter
    );
  });

  const setStatusFilterSafe = (next) => {
    const arr = Array.isArray(next) ? next : [];
    statusFilterRef.current = arr;
    setStatusFilter(arr);
  };

  const setBvFilterSafe = (next) => {
    const arr = Array.isArray(next) ? next : [];
    bvFilterRef.current = arr;
    setBvFilter(arr);
  };

  const setInvoiceSearchSafe = (next) => {
    invoiceSearchRef.current = String(next ?? "").trim();
  };

  useEffect(() => {
    categoryFilterRef.current = categoryFilter;
  }, [categoryFilter]);
  useEffect(() => {
    customerFilterRef.current = customerFilter;
  }, [customerFilter]);
  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);
  useEffect(() => {
    endDateRef.current = endDate;
  }, [endDate]);
  useEffect(() => {
    listLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
    };
  }, [location.pathname, location.search]);

  // Fetch SR status list from Greenlam.SRSTATUSMASTER
  useEffect(() => {
    let mounted = true;
    complaintAPI
      .getSRStatuses()
      .then((res) => {
        if (!mounted || !res?.data) return;
        const options = (res.data || []).map((s) => ({
          id: s.code || s.SRStatusCODE || "",
          text: s.name || s.NAME || s.code || s.SRStatusCODE || "",
        })).filter((o) => o.id);
        setStatusOptions(options);
      })
      .catch(() => setStatusOptions([]));
    return () => { mounted = false; };
  }, []);

  // Fetch business verticals from Greenlam.CATEGORYMASTER (via masterDataAPI)
  useEffect(() => {
    let mounted = true;
    masterDataAPI
      .getVerticals()
      .then((res) => {
        if (!mounted) return;
        const verticalList = res?.success && Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];

        const options = verticalList
          .map((bv) => ({
            id: bv?.bvCode ?? bv?.BVCode ?? bv?.BVCODE ?? "",
            text: bv?.bvName ?? bv?.BVName ?? bv?.BVNAME ?? "",
          }))
          .filter((o) => o.id && o.text);

        setBvOptions(options);
      })
      .catch(() => {
        if (mounted) setBvOptions([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Restore saved list filters when returning from SR detail; otherwise read URL query params.
  useEffect(() => {
    const searchParamsEarly = new URLSearchParams(location.search);
    const drillQueryToRespond =
      searchParamsEarly.get("queryToRespond") === "true" ||
      searchParamsEarly.get("queryToRespond") === "1" ||
      String(searchParamsEarly.get("queryToRespond") || "").toLowerCase() === "true";
    const drillSpecialCase =
      searchParamsEarly.get("specialCase") === "true" ||
      searchParamsEarly.get("specialCase") === "1" ||
      String(searchParamsEarly.get("specialCase") || "").toLowerCase() === "true";
    // Dashboard card drill-down must win over a stale restore snapshot.
    const shouldRestore =
      !drillQueryToRespond &&
      !drillSpecialCase &&
      (Boolean(location.state?.restoreComplaintsList) ||
        Boolean(readComplaintsListRestoreSnapshot()));

    if (shouldRestore) {
      const saved = readComplaintsListState();
      if (saved) {
        if (Array.isArray(saved.statusFilter)) {
          setStatusFilterSafe(saved.statusFilter);
        }
        if (saved.categoryFilter != null) {
          setCategoryFilter(String(saved.categoryFilter || ""));
        }
        if (saved.customerFilter != null) {
          setCustomerFilter(String(saved.customerFilter || ""));
        }
        if (Array.isArray(saved.bvFilter)) {
          setBvFilterSafe(saved.bvFilter);
        }
        if (saved.startDate) setStartDate(saved.startDate);
        if (saved.endDate) setEndDate(saved.endDate);
        if (saved.invoiceSearch != null) {
          setInvoiceSearchSafe(String(saved.invoiceSearch || ""));
        }
        if (saved.startDate && saved.endDate) {
          const displayText = `${moment(saved.startDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(
            saved.endDate,
            "YYYY-MM-DD"
          ).format("DD-MM-YYYY")}`;
          setDateRangeText(displayText);
          if (dateRangePickerRef.current) {
            $(dateRangePickerRef.current).val(displayText);
          }
        }
        if (
          (Array.isArray(saved.statusFilter) && saved.statusFilter.length > 0) ||
          (Array.isArray(saved.bvFilter) && saved.bvFilter.length > 0) ||
          saved.categoryFilter ||
          saved.customerFilter
        ) {
          setIsFilterCollapsed(false);
        }
        restoreAppliedRef.current = true;
      }
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get("status");
    const categoryParam = searchParams.get("category");
    const bvParam = searchParams.get("bv");
    const customerParam = searchParams.get("customer");
    const customerCodeParam = searchParams.get("customerCode");
    const fromDateParam = searchParams.get("fromDate");
    const toDateParam = searchParams.get("toDate");
    const invoiceParam = readInvoiceFromSearchParams(searchParams);
    setInvoiceSearchSafe(invoiceParam);

    if (statusParam) {
      const raw = String(statusParam).trim();
      // Dashboard Open card sends `status=1` Ã¢â€ â€™ Pending only (matches OpenCount).
      if (raw === "1") {
        setStatusFilterSafe(["Pending"]);
      } else if (raw === "7") {
        // Dashboard WIP card sends `status=7`.
        setStatusFilterSafe(["WIP"]);
      } else {
        const mapped = STATUS_QUERY_MAP[raw] || raw;
        setStatusFilterSafe([mapped]);
      }
    } else {
      setStatusFilterSafe([]);
    }

    // Query / Special Case drill-down: clear status so client filter does not hide SP rows.
    const queryToRespondUrl = searchParams.get("queryToRespond");
    const specialCaseUrl = searchParams.get("specialCase");
    if (
      queryToRespondUrl === "true" ||
      queryToRespondUrl === "1" ||
      String(queryToRespondUrl || "").toLowerCase() === "true" ||
      specialCaseUrl === "true" ||
      specialCaseUrl === "1" ||
      String(specialCaseUrl || "").toLowerCase() === "true"
    ) {
      setStatusFilterSafe([]);
    }

    if (categoryParam) {
      setCategoryFilter(decodeURIComponent(categoryParam));
    } else {
      setCategoryFilter("");
    }

    if (bvParam) {
      const decoded = decodeURIComponent(bvParam);
      const parts = decoded
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      setBvFilterSafe(parts);
    } else {
      setBvFilterSafe([]);
    }

    if (customerParam) {
      setCustomerFilter(decodeURIComponent(customerParam));
    } else if (customerCodeParam) {
      setCustomerFilter(decodeURIComponent(customerCodeParam));
    }

    // If drilldown provides date range, align the complaints page with it (so counts match dashboard).
    const from = fromDateParam ? decodeURIComponent(fromDateParam) : "";
    const to = toDateParam ? decodeURIComponent(toDateParam) : "";
    const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
    if (isIso(from)) setStartDate(from);
    if (isIso(to)) setEndDate(to);
    if (isIso(from) && isIso(to)) {
      const displayText = `${moment(from, "YYYY-MM-DD").format("DD-MM-YYYY")} - ${moment(
        to,
        "YYYY-MM-DD"
      ).format("DD-MM-YYYY")}`;
      setDateRangeText(displayText);
      if (dateRangePickerRef.current) {
        $(dateRangePickerRef.current).val(displayText);
      }
    }
  }, [location.search, location.pathname, location.key, location.state?.restoreComplaintsList]);

  // Normalize route-derived status values to dropdown option IDs once options load.
  // This lets us keep URL values (e.g., "1") mapped to dropdown display text (e.g., "Pending")
  // while still selecting the correct `id` values inside CustomMultiSelect.
  useEffect(() => {
    if (!Array.isArray(statusOptions) || statusOptions.length === 0) return;
    if (!Array.isArray(statusFilter) || statusFilter.length === 0) return;

    const normalized = statusFilter
      .map((v) => String(v ?? "").trim())
      .filter(Boolean)
      .map((v) => {
        const byId = statusOptions.find(
          (o) => String(o.id ?? "").trim().toLowerCase() === v.toLowerCase()
        );
        if (byId) return String(byId.id);

        const byText = statusOptions.find(
          (o) => String(o.text ?? "").trim().toLowerCase() === v.toLowerCase()
        );
        if (byText) return String(byText.id);

        return v;
      });

    const current = statusFilter.map((v) => String(v ?? "").trim()).filter(Boolean);
    const same =
      current.length === normalized.length &&
      current.every(
        (v, i) => v.toLowerCase() === String(normalized[i] ?? "").trim().toLowerCase()
      );

    if (!same) setStatusFilterSafe(normalized);
  }, [statusOptions, statusFilter]);

  // If URL query `bv` contains bvName instead of bvCode, normalize it to bvCode for the dropdown.
  useEffect(() => {
    if (!Array.isArray(bvFilter) || bvFilter.length === 0) return;
    if (bvOptions.length === 0) return;

    const normalized = bvFilter
      .map((v) => (v === null || v === undefined ? "" : String(v)).trim())
      .filter(Boolean)
      .map((target) => {
        const targetLower = target.toLowerCase();
        const byId = bvOptions.find((o) => String(o.id).trim().toLowerCase() === targetLower);
        if (byId) return byId.id;

        const byText = bvOptions.find((o) => String(o.text).trim().toLowerCase() === targetLower);
        if (byText) return byText.id;

        // Keep as-is if it doesn't match any option yet
        return target;
      });

    // De-dup while preserving order
    const seen = new Set();
    const deduped = [];
    normalized.forEach((v) => {
      const key = String(v).trim();
      if (!key || seen.has(key.toLowerCase())) return;
      seen.add(key.toLowerCase());
      deduped.push(v);
    });

    const current = bvFilter.map((v) => String(v).trim()).filter(Boolean);
    const same =
      current.length === deduped.length &&
      current.every((v, i) => String(v).trim().toLowerCase() === String(deduped[i]).trim().toLowerCase());

    if (!same) setBvFilterSafe(deduped);
  }, [bvFilter, bvOptions]);

  const mapSrListItems = (response) => {
    if (
      !response ||
      !response.success ||
      !response.data ||
      !Array.isArray(response.data.items)
    ) {
      return [];
    }
    return response.data.items.map((d, index) => {
      const id =
        d.srCode ||
        d.SRCode ||
        d.srNumber ||
        d.SRNumber ||
        d.id ||
        d.Id ||
        index + 1;
      return {
        id: d.srCode || d.SRCode || id,
        srNumber: d.srNo || d.SRNo || d.srNumber || d.SRNumber || id,
        statusName: d.status || d.Status || "",
        createdByName: d.raisedBy || d.RaisedBy || "",
        assignedToName: d.assignedTo || d.AssignedTo || "",
        assignToRoleId: d.assignToRoleId || d.AssignToRoleId || "",
        assignToUserId: d.assignToUserId || d.AssignToUserId || "",
        assignToUserName: d.assignToUserName || d.AssignToUserName || "",
        assignToUserTypeCode: d.assignToUserTypeCode || d.AssignToUserTypeCode || "",
        assignToUserFullName: d.assignToUserFullName || d.AssignToUserFullName || "",
        srCategory: d.srCategory || d.SRCategory || "",
        category: d.category || d.Category || "",
        invoiceNumber: d.invoiceNumber || d.InvoiceNumber || "",
        customerCode:
          d.customerCode || d.CustomerCode || d.customercode || d.CUSTOMERCODE || "",
        customerName: d.customerName || d.CustomerName || "",
        invoiceDate: d.invoiceDate || d.InvoiceDate || null,
        srRaisedDate:
          d.srDate || d.SRDate || d.srRaisedDate || d.SRRaisedDate || null,
      };
    });
  };

  const bindSrDataTable = (items) => {
    if (!srTableRef.current) return;
    if (!$.fn.DataTable) {
      DataTable($);
    }
    if (dataTableInstanceRef.current) {
      dataTableInstanceRef.current.destroy();
      dataTableInstanceRef.current = null;
    }

    dataTableInstanceRef.current = $(srTableRef.current).DataTable({
      data: items,
      paging: true,
      searching: true,
      info: true,
      lengthChange: true,
      lengthMenu: [5, 10, 25, 50, 100],
      pageLength: 10,
      dom: "lfrtip",
      ordering: true,
      destroy: true,
      scrollX: true,
      autoWidth: false,
      fixedColumns: false,
      columns: [
        {
          title: "SL No",
          data: null,
          render: (d, type, row, meta) => (meta.row + 1).toString(),
          className: "text-center",
        },
        {
          title: "SR No",
          data: null,
          render: (d) => {
            const parentSrCode = d.srCode || d.id;
            const childSrCode = d.srNumber || d.srNo || "";
            const num = d.srNumber || d.srNo || parentSrCode;
            return renderSrLinkHtml({
              parentSrCode,
              childSrCode,
              label: num,
            });
          },
          className: "text-center",
        },
        {
          title: "SR Date",
          data: null,
          render: (d) => {
            const dt = d.srRaisedDate || d.srDate;
            if (!dt) return "";
            const dte = typeof dt === "string" ? new Date(dt) : dt;
            return moment(dte).isValid() ? moment(dte).format("DD-MM-YYYY") : "";
          },
          className: "text-center",
        },
        {
          title: "Invoice No",
          data: null,
          render: (d) => d.invoiceNumber || "",
          className: "text-center",
        },
        {
          title: "Business Vertical",
          data: null,
          render: (d) => d.category || d.Category || "",
        },
        {
          title: "SR Category",
          data: null,
          render: (d) => d.srCategory || d.SRCategory || "",
        },
        {
          title: "Raised By",
          data: null,
          render: (d) => d.createdByName || d.raisedBy || "",
        },
        {
          title: "Assigned To / Last Actioned By",
          data: null,
          render: (d) =>
            `${d.assignToUserFullName || d.assignToUserName || d.assignedToName || ""}${
              d.assignToRoleId ? ` (${d.assignToRoleId})` : ""
            }`.trim(),
        },
        {
          title: "status",
          data: null,
          className: "text-center",
          render: (d) => {
            const status =
              d.statusName || d.StatusName || d.status || d.Status || "";
            return renderStatusHtml(status);
          },
        },
      ],
      rowCallback: function (row, data) {
        const isTATExceeded =
          data.isTATExceeded ||
          data.IsTATExceeded ||
          data.tatExceeded ||
          data.TATExceeded ||
          data.slaBreached ||
          data.SlaBreached ||
          data.isSlaBreached ||
          data.IsSlaBreached ||
          false;
        if (isTATExceeded) {
          $(row).addClass("tat-exceeded-row");
          $(row).find("td").css({
            color: "#dc143c",
            textShadow:
              "0 0 1px rgba(220, 20, 60, 0.3), 0 1px 2px rgba(220, 20, 60, 0.2)",
            fontWeight: "500",
          });
          $(row).find("a").css({
            color: "#dc143c",
            textShadow:
              "0 0 1px rgba(220, 20, 60, 0.3), 0 1px 2px rgba(220, 20, 60, 0.2)",
          });
        }
      },
      initComplete: function () {
        try {
          const wrapper = $(srTableRef.current).closest(
            ".dataTables_wrapper, .dt-container"
          );
          if (wrapper.length > 0) {
            const lengthEl = wrapper.find(".dt-length");
            const searchEl = wrapper.find(".dt-search");
            if (lengthEl.length > 0 || searchEl.length > 0) {
              let topWrap = wrapper.find(".dtFltrTop");
              if (topWrap.length === 0) {
                topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                wrapper.prepend(topWrap);
              }
              lengthEl.each(function () {
                $(this).appendTo(topWrap);
              });
              searchEl.each(function () {
                $(this).appendTo(topWrap);
              });
            }
            const infoEl = wrapper.find(".dt-info");
            const pagingEl = wrapper.find(".dt-paging");
            if (infoEl.length > 0 || pagingEl.length > 0) {
              let bottomWrap = wrapper.find(".dtFltrBotm");
              if (bottomWrap.length === 0) {
                bottomWrap = $("<div class='dtFltrBotm d-flex flex-wrap'></div>");
                wrapper.append(bottomWrap);
              }
              infoEl.each(function () {
                $(this).appendTo(bottomWrap);
              });
              pagingEl.each(function () {
                $(this).appendTo(bottomWrap);
              });
            }
          }
        } catch (error) {
          console.error("Error in initComplete:", error);
        }
      },
    });

    $(srTableRef.current).off("click", ".sr-link");
    $(srTableRef.current).on("click", ".sr-link", function (e) {
      e.preventDefault();
      const parentSrCode = $(this).data("parentSrCode");
      const childSrCode = $(this).data("childSrCode");
      if (!parentSrCode && !childSrCode) return;
      const parent = parentSrCode || childSrCode;
      const query = childSrCode ? `?childSrCode=${encodeURIComponent(childSrCode)}` : "";
      markBcmServiceRequestListRestored();
      saveComplaintsListState({
        pendingRestore: true,
        statusFilter: Array.isArray(statusFilterRef.current)
          ? [...statusFilterRef.current]
          : [],
        categoryFilter: categoryFilterRef.current,
        customerFilter: customerFilterRef.current,
        bvFilter: Array.isArray(bvFilterRef.current) ? [...bvFilterRef.current] : [],
        startDate: startDateRef.current,
        endDate: endDateRef.current,
        invoiceSearch: invoiceSearchRef.current || "",
        returnPath: `${listLocationRef.current.pathname || "/complaints"}${listLocationRef.current.search || ""}`,
      });
      navigate(`/complaints/${encodeURIComponent(parent)}${query}`);
    });
  };

  useEffect(() => {
    const seq = ++loadSeqRef.current;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const searchParams = new URLSearchParams(location.search);
        const statusParam = searchParams.get("status");
        const customerCodeParam = searchParams.get("customerCode");
        const bvParam = searchParams.get("bv");
        const fromDateParam = searchParams.get("fromDate");
        const toDateParam = searchParams.get("toDate");
        const categoryParam = searchParams.get("category");
        const invoiceParam = readInvoiceFromSearchParams(searchParams);
        const queryToRespondParam = searchParams.get("queryToRespond");
        const specialCaseParam = searchParams.get("specialCase");
        const onlyQueriesToRespondByMe =
          queryToRespondParam === "true" ||
          queryToRespondParam === "1" ||
          String(queryToRespondParam || "").toLowerCase() === "true";
        const onlySpecialCase =
          specialCaseParam === "true" ||
          specialCaseParam === "1" ||
          String(specialCaseParam || "").toLowerCase() === "true";

        const decodeCsv = (v) =>
          String(v || "")
            .split(",")
            .map((s) => decodeURIComponent(String(s || "")).trim())
            .filter(Boolean)
            .join(",");

        // Map numeric dashboard-card status codes to name strings the API expects
        const mapStatusToName = (v) => {
          const s = String(v || "").trim();
          return STATUS_QUERY_MAP[s] || s;
        };
        const statusCodesCsvForApi =
          onlyQueriesToRespondByMe || onlySpecialCase
            ? undefined
            : statusParam
              ? String(statusParam || "")
                  .split(",")
                  .map((s) => mapStatusToName(decodeURIComponent(String(s || "").trim())))
                  .filter(Boolean)
                  .join(",")
              : undefined;
        const businessVerticalCodesCsvForApi = bvParam ? decodeCsv(bvParam) : undefined;

        const isIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
        const fromDateForApi = fromDateParam ? decodeURIComponent(fromDateParam).trim() : "";
        const toDateForApi = toDateParam ? decodeURIComponent(toDateParam).trim() : "";

        // When the URL has no date range (e.g. dashboard card clicks), fall back to
        // the current Financial Year so the query is not open-ended.
        const fyFallback = getCurrentFinancialYearRange();
        const refFrom = String(startDateRef.current || "").trim();
        const refTo = String(endDateRef.current || "").trim();
        const effectiveFromDate = isIso(fromDateForApi)
          ? fromDateForApi
          : isIso(refFrom)
            ? refFrom
            : fyFallback.start;
        const effectiveToDate = isIso(toDateForApi)
          ? toDateForApi
          : isIso(refTo)
            ? refTo
            : fyFallback.end;

        // Call backend SR list API (SR tables)
        const categoryForApi = categoryParam
          ? decodeURIComponent(categoryParam).trim()
          : undefined;

        const searchTextForApi = invoiceParam || undefined;

        const response = await complaintAPI.getServiceRequestList({
          createdBy: createdByForList || undefined,
          customerCode: customerCodeParam ? decodeURIComponent(customerCodeParam) : undefined,
          statusCodesCsv: statusCodesCsvForApi || undefined,
          businessVerticalCodesCsv: businessVerticalCodesCsvForApi || undefined,
          category: categoryForApi,
          fromDate: effectiveFromDate,
          toDate: effectiveToDate,
          searchText: searchTextForApi || undefined,
          queryToRespond: onlyQueriesToRespondByMe || undefined,
          specialCase: onlySpecialCase || undefined,
          pageNumber: 1,
          pageSize: 500,
        });

        if (seq !== loadSeqRef.current) return;

        const items = mapSrListItems(response);
        setAllData(items);
        setFilteredData(items);

        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        if (seq !== loadSeqRef.current) return;

        // Apply URL/dashboard filters (invoice) before binding grid; status/BV/date use handleFilter.
        let rowsForGrid = items;
        if (invoiceParam) {
          const needle = String(invoiceParam).trim();
          rowsForGrid = items.filter((item) => {
            const inv = String(item.invoiceNumber || item.InvoiceNumber || "").trim();
            const srNo = String(
              item.srNumber || item.SRNumber || item.srNo || item.SRNo || ""
            ).trim();
            return (
              inv === needle ||
              inv.includes(needle) ||
              srNo === needle ||
              srNo.includes(needle)
            );
          });
        }
        bindSrDataTable(rowsForGrid);
        handleFilter(items);
        if (restoreAppliedRef.current) {
          markComplaintsListRestored();
          restoreAppliedRef.current = false;
        }
      } catch (err) {
        console.error("Failed to load SRs", err);
        if (seq === loadSeqRef.current) {
          setLoadError(
            getApiErrorMessage(
              err,
              "Unable to load service requests. The API may be slow or unavailable."
            )
          );
          setAllData([]);
          setFilteredData([]);
        }
      } finally {
        if (seq === loadSeqRef.current) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      loadSeqRef.current += 1;
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
      if (srTableRef.current) {
        $(srTableRef.current).off("click", ".sr-link");
      }
    };
  }, [navigate, createdByForList, location.search]);

  // Filter handlers Ã¢â‚¬â€ status from SRSTATUSMASTER (code), date uses SR raised date
  // Read current status from Select2 on apply so button works even before state has updated
  const handleFilter = (sourceData = allData) => {
    // Debugging aid: confirm filter button/selection is invoking this function
    // (helps verify whether click is firing and whether state has updated)
    console.log("Complaints handleFilter()", {
      statusFilter: statusFilterRef.current,
      bvFilter: bvFilterRef.current,
      categoryFilter,
      startDate,
      endDate,
      allDataCount: Array.isArray(sourceData) ? sourceData.length : 0,
    });

    let filtered = Array.isArray(sourceData) ? [...sourceData] : [];
    const urlDrilldown = getUrlDrilldownParams();
    const currentStatusFilter = Array.isArray(statusFilterRef.current)
      ? statusFilterRef.current
      : [];

    // Filter by status (array of status codes from SRSTATUSMASTER)
    if (currentStatusFilter && currentStatusFilter.length > 0) {
      const isPaymentPendingOnly =
        currentStatusFilter.length === 1 &&
        String(currentStatusFilter[0] || "").trim().toLowerCase() === "payment pending";
      if (isPaymentPendingOnly) {
        // Server already applies Payment Pending special logic; avoid re-filtering client-side by Status text.
      } else {
      const selectedCodes = currentStatusFilter.map((id) => String(id).trim()).filter(Boolean);
      const selectedTexts = selectedCodes.map((c) => {
        const opt = statusOptions.find((o) => String(o.id).trim() === c);
        return opt?.text ?? c;
      });

      const accepted = new Set(
        [...selectedCodes, ...selectedTexts]
          .filter(Boolean)
          .map((v) => String(v).trim().toLowerCase())
      );

      filtered = filtered.filter((item) => {
        const code = (item.statusName || item.StatusName || item.status || item.Status || "").toString().trim();
        const codeLower = code.toLowerCase();
        return Array.from(accepted).some((v) => codeLower === v || codeLower.includes(v));
      });
      }
    }

    // Dashboard drill-down: invoice / SR search (SP may not filter until migration applied).
    const invoiceNeedle =
      invoiceSearchRef.current ||
      urlDrilldown.invoiceNo ||
      readInvoiceFromSearchParams(new URLSearchParams(location.search));
    if (invoiceNeedle) {
      const needle = String(invoiceNeedle).trim();
      filtered = filtered.filter((item) => {
        const inv = String(item.invoiceNumber || item.InvoiceNumber || "").trim();
        const srNo = String(
          item.srNumber || item.SRNumber || item.srNo || item.SRNo || ""
        ).trim();
        return (
          inv === needle ||
          inv.includes(needle) ||
          srNo === needle ||
          srNo.includes(needle)
        );
      });
    }

    // Filter by SR Category (dashboard chart drill-down); exact match on SRCategory column.
    if (categoryFilter) {
      const want = String(categoryFilter).trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const srCat = String(
          item.srCategory || item.SRCategory || ""
        )
          .trim()
          .toLowerCase();
        return srCat === want;
      });
    }

    // Filter by Business Vertical (value from Greenlam.CATEGORYMASTER)
    const currentBvFilter = Array.isArray(bvFilterRef.current)
      ? bvFilterRef.current
      : [];
    if (Array.isArray(currentBvFilter) && currentBvFilter.length > 0) {
      const selectedBvs = bvOptions.filter((o) =>
        currentBvFilter.some(
          (v) =>
            String(o.id).trim().toLowerCase() === String(v).trim().toLowerCase() ||
            String(o.text).trim().toLowerCase() === String(v).trim().toLowerCase()
        )
      );

      const acceptedValues = new Set();
      currentBvFilter.forEach((v) => acceptedValues.add(String(v).trim()));
      selectedBvs.forEach((sbv) => {
        acceptedValues.add(String(sbv.id).trim());
        acceptedValues.add(String(sbv.text).trim());
      });

      const accepted = Array.from(acceptedValues)
        .filter(Boolean)
        .map((v) => v.toString().trim().toLowerCase());

      filtered = filtered.filter((item) => {
        const itemBv =
          item.category || item.Category || item.bvCode || item.BVCode || item.bvName || item.BVName || "";
        const itemBvStr = String(itemBv).trim().toLowerCase();
        if (!itemBvStr) return false;
        return accepted.some((v) => itemBvStr === v || itemBvStr.includes(v));
      });
    }

    // Filter by customer (manual filter / dashboard ?customer= name)
    // Skip when ?customerCode= is present Ã¢â‚¬â€ API/SP already filtered by @CustomerCode.
    if (
      customerFilter &&
      String(customerFilter).trim() &&
      !urlDrilldown.serverFilteredByCustomer
    ) {
      const needle = String(customerFilter).trim().toLowerCase();
      const codeNeedle = urlDrilldown.customerCode
        ? urlDrilldown.customerCode.toLowerCase()
        : "";
      const hasCustomerFields = filtered.some((item) => {
        const name = String(item.customerName || item.CustomerName || "").trim();
        const code = String(item.customerCode || item.CustomerCode || "").trim();
        return Boolean(name || code);
      });
      if (!hasCustomerFields) {
        console.warn(
          "Customer filter requested, but dataset has no customer fields. Skipping filter.",
          { customerFilter }
        );
      } else {
        filtered = filtered.filter((item) => {
          const name = String(item.customerName || item.CustomerName || "")
            .trim()
            .toLowerCase();
          const code = String(item.customerCode || item.CustomerCode || "")
            .trim()
            .toLowerCase();
          return (
            (name && (name === needle || name.includes(needle))) ||
            (code && (code === needle || code.includes(needle))) ||
            (codeNeedle && code === codeNeedle)
          );
        });
      }
    }

    // Filter by date range (SR raised date)
    const itemDateField = (item) => {
      const d = item.srRaisedDate || item.srDate || item.SRDate || item.SRRaisedDate || item.createdDate || item.CreatedDate;
      if (!d) return null;
      // Support API date variants reliably (ISO, SQL datetime, and display formats).
      const m = moment(
        d,
        [
          moment.ISO_8601,
          "YYYY-MM-DD HH:mm:ss.SSS",
          "YYYY-MM-DD HH:mm:ss",
          "YYYY-MM-DD",
          "DD-MM-YYYY",
          "DD/MM/YYYY",
        ],
        true
      );
      if (m.isValid()) return m.toDate();
      const fallback = new Date(d);
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    };
    // Skip when ?fromDate=&toDate= in URL Ã¢â‚¬â€ API/SP already filtered by @FromDate/@ToDate.
    if (!urlDrilldown.serverFilteredByDate && startDate && endDate) {
      const start = moment(startDate, "YYYY-MM-DD", true).startOf("day").toDate();
      const end = moment(endDate, "YYYY-MM-DD", true).endOf("day").toDate();
      filtered = filtered.filter((item) => {
        const itemDate = itemDateField(item);
        return itemDate && itemDate >= start && itemDate <= end;
      });
    } else if (!urlDrilldown.serverFilteredByDate && startDate) {
      const start = moment(startDate, "YYYY-MM-DD", true).startOf("day").toDate();
      filtered = filtered.filter((item) => {
        const itemDate = itemDateField(item);
        return itemDate && itemDate >= start;
      });
    } else if (!urlDrilldown.serverFilteredByDate && endDate) {
      const end = moment(endDate, "YYYY-MM-DD", true).endOf("day").toDate();
      filtered = filtered.filter((item) => {
        const itemDate = itemDateField(item);
        return itemDate && itemDate <= end;
      });
    }

    setFilteredData(filtered);
    console.log("Complaints handleFilter() filtered rows:", filtered.length);

    // Update DataTable with filtered rows
    const dt = dataTableInstanceRef.current;
    const $table = srTableRef.current ? $(srTableRef.current) : null;
    if (dt && $table && $table.length) {
      try {
        dt.clear();
        dt.rows.add(filtered);
        dt.draw();
      } catch (err) {
        console.warn("Filter: DataTable update failed", err);
      }
    }
  };

  // Filter button click: re-fetch backend dataset (server-side date + single status)
  // then apply Business Vertical + multi-status client-side and redraw grid.
  const handleFilterAndRefresh = async () => {
    setLoading(true);
    try {
      const statusCodeForApi =
        Array.isArray(statusFilterRef.current) && statusFilterRef.current.length === 1
          && String(statusFilterRef.current[0] || "").trim().toLowerCase() !== "payment pending"
          ? (statusOptions.find(
              (o) =>
                String(o.id).trim().toLowerCase() ===
                String(statusFilterRef.current[0]).trim().toLowerCase()
            )?.text ?? statusFilterRef.current[0])
          : undefined;
      const statusCodesCsvForApi =
        Array.isArray(statusFilterRef.current) && statusFilterRef.current.length > 0
          ? statusFilterRef.current
              .map((v) => {
                const match = statusOptions.find(
                  (o) =>
                    String(o.id).trim().toLowerCase() ===
                    String(v).trim().toLowerCase()
                );
                return match?.text ?? v;
              })
              .join(",")
          : undefined;

      const businessVerticalCodesCsvForApi =
        Array.isArray(bvFilterRef.current) && bvFilterRef.current.length > 0
          ? bvFilterRef.current.join(",")
          : undefined;

      const urlCategory = new URLSearchParams(location.search).get("category");
      const categoryForApi = urlCategory
        ? decodeURIComponent(urlCategory).trim()
        : categoryFilter
          ? String(categoryFilter).trim()
          : undefined;

      const searchTextForApi =
        invoiceSearchRef.current ||
        readInvoiceFromSearchParams(new URLSearchParams(location.search)) ||
        undefined;

      const urlSearch = new URLSearchParams(location.search);
      const queryToRespondParam = urlSearch.get("queryToRespond");
      const specialCaseParam = urlSearch.get("specialCase");
      const onlyQueriesToRespondByMe =
        queryToRespondParam === "true" ||
        queryToRespondParam === "1" ||
        String(queryToRespondParam || "").toLowerCase() === "true";
      const onlySpecialCase =
        specialCaseParam === "true" ||
        specialCaseParam === "1" ||
        String(specialCaseParam || "").toLowerCase() === "true";

      const response = await complaintAPI.getServiceRequestList({
        createdBy: createdByForList || undefined,
        customerCode:
          urlSearch.get("customerCode") || undefined,
        statusCode: onlyQueriesToRespondByMe || onlySpecialCase ? undefined : statusCodeForApi || undefined,
        statusCodesCsv: onlyQueriesToRespondByMe || onlySpecialCase ? undefined : statusCodesCsvForApi || undefined,
        businessVerticalCodesCsv:
          businessVerticalCodesCsvForApi || undefined,
        category: categoryForApi || undefined,
        fromDate: startDate || undefined,
        toDate: endDate || undefined,
        searchText: searchTextForApi || undefined,
        queryToRespond: onlyQueriesToRespondByMe || undefined,
        specialCase: onlySpecialCase || undefined,
        pageNumber: 1,
        pageSize: 500,
      });

      const items = mapSrListItems(response);
      setAllData(items);
      handleFilter(items);
    } catch (err) {
      console.error("Filter refresh failed", err);
      setLoadError(getApiErrorMessage(err, "Filter could not load service requests."));
    } finally {
      setLoading(false);
    }
  };

  // Auto-apply filter when filters or data change (including default date range)
  useEffect(() => {
    if (allData.length > 0) {
      handleFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter, customerFilter, bvFilter, startDate, endDate, allData.length, bvOptions]);

  const handleReset = () => {
    clearComplaintsListState();
    setStatusFilterSafe([]);
    setCategoryFilter("");
    setCustomerFilter("");
    setBvFilterSafe([]);
    const fy = getCurrentFinancialYearRange();
    setStartDate(fy.start);
    setEndDate(fy.end);
    setDateRangeText(fy.displayText);
    if (dateRangePickerRef.current) {
      $(dateRangePickerRef.current).val(fy.displayText);
    }
    // useEffect will run handleFilter when startDate/endDate update
  };

  // Helper function to get financial year start (April 1st)
  const getFinancialYearStart = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    if (month < 3) {
      // Jan-Mar: FY starts previous year April
      return new Date(year - 1, 3, 1);
    }
    return new Date(year, 3, 1);
  };

  // Helper function to get quarter start
  const getQuarterStart = (date) => {
    const month = date.getMonth();
    const quarter = Math.floor(month / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
  };

  // Helper function to get week start (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // Initialize daterangepicker with predefined ranges (only when filter panel is expanded Ã¢â‚¬â€ input not in DOM when collapsed)
  useEffect(() => {
    if (isFilterCollapsed || !dateRangePickerRef.current) return;

    const timer = setTimeout(() => {
      const $input = $(dateRangePickerRef.current);
      if (!$input.length) return;

      const initDateRangePicker = () => {
        if ($input.data("daterangepicker")) {
          $input.data("daterangepicker").remove();
        }

        const today = moment();
        const yesterday = moment().subtract(1, "days");
        const thisWeekStart = getWeekStart(new Date());
        const lastWeekStart = moment(thisWeekStart).subtract(7, "days");
        const lastWeekEnd = moment(thisWeekStart).subtract(1, "days");
        const thisMonthStart = moment().startOf("month");
        const lastMonthStart = moment().subtract(1, "month").startOf("month");
        const lastMonthEnd = moment().subtract(1, "month").endOf("month");
        const thisQuarterStart = getQuarterStart(new Date());
        const lastQuarterStart = moment(thisQuarterStart).subtract(3, "months");
        const lastQuarterEnd = moment(thisQuarterStart).subtract(1, "days");
        const thisFYStart = getFinancialYearStart(new Date());
        const thisFYEnd = moment(thisFYStart).add(1, "year").subtract(1, "day"); // 31-Mar of same FY
        const lastFYStart = moment(thisFYStart).subtract(1, "year");
        const lastFYEnd = moment(thisFYStart).subtract(1, "days");

        try {
          $input.daterangepicker({
            autoUpdateInput: false,
            locale: {
              cancelLabel: "Clear",
              applyLabel: "Apply",
              format: "DD-MM-YYYY",
            },
            opens: "left",
            drops: "down",
            showDropdowns: true,
            ranges: {
              Today: [today, today],
              Yesterday: [yesterday, yesterday],
              "This Week": [moment(thisWeekStart), today],
              "Last Week": [lastWeekStart, lastWeekEnd],
              "This Month": [thisMonthStart, today],
              "Last Month": [lastMonthStart, lastMonthEnd],
              "This Quarter": [moment(thisQuarterStart), today],
              "Last Quarter": [lastQuarterStart, lastQuarterEnd],
              "This Financial Year": [moment(thisFYStart), thisFYEnd],
              "Last Financial Year": [lastFYStart, lastFYEnd],
            },
          });

          $input.off("apply.daterangepicker");
          $input.off("cancel.daterangepicker");
          $input.on("apply.daterangepicker", function (ev, picker) {
            const start = picker.startDate.format("YYYY-MM-DD");
            const end = picker.endDate.format("YYYY-MM-DD");
            const displayText =
              picker.startDate.format("DD-MM-YYYY") +
              " - " +
              picker.endDate.format("DD-MM-YYYY");

            setStartDate(start);
            setEndDate(end);
            setDateRangeText(displayText);
            $(this).val(displayText);
          });

          $input.on("cancel.daterangepicker", function (ev, picker) {
            setStartDate("");
            setEndDate("");
            setDateRangeText("");
            $(this).val("");
          });
        } catch (error) {
          console.error("Error initializing daterangepicker:", error);
        }
      };

      initDateRangePicker();
      const displayText =
        dateRangeText ||
        (startDate && endDate
          ? moment(startDate).format("DD-MM-YYYY") +
            " - " +
            moment(endDate).format("DD-MM-YYYY")
          : defaultFY.displayText);
      $input.val(displayText);

      const handleInteraction = () => {
        if (!$input.data("daterangepicker")) {
          initDateRangePicker();
        }
      };

      $input.off("click.daterangepicker-reinit");
      $input.off("focus.daterangepicker-reinit");
      $input.on("click.daterangepicker-reinit", handleInteraction);
      $input.on("focus.daterangepicker-reinit", handleInteraction);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (dateRangePickerRef.current) {
        const $input = $(dateRangePickerRef.current);
        $input.off("click.daterangepicker-reinit");
        $input.off("focus.daterangepicker-reinit");
        if ($input.data("daterangepicker")) {
          $input.off("apply.daterangepicker");
          $input.off("cancel.daterangepicker");
          $input.data("daterangepicker").remove();
        }
      }
    };
  }, [isFilterCollapsed]);

  // Handle sidebar toggle - recalculate DataTable columns
  useEffect(() => {
    if (dataTableInstanceRef.current) {
      // Wait for CSS transition to complete (300ms as defined in App.js)
      const timer = setTimeout(() => {
        try {
          dataTableInstanceRef.current.columns.adjust().draw(false);
        } catch (error) {
          console.error("Error adjusting DataTable columns:", error);
        }
      }, 350); // 350ms to ensure transition completes

      return () => clearTimeout(timer);
    }
  }, [sidebarOpen]);

  return (
    <Box>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 2 }}
        open={loading}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading service requestsÃ¢â‚¬Â¦
          </Typography>
        </Box>
      </Backdrop>
      {loadError && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          className="service-request-heading"
          sx={{ color: "#333333", fontWeight: 600 }}
        >
          Service Request List
        </Typography>
        {hasUserType("SE") && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/raise-complaint")}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Raise SR
          </Button>
        )}
      </Box>

      {/* Filter Panel */}
      <div
        className="card-panel mb-3 filter-panel"
        style={{ paddingBottom: isFilterCollapsed ? "2px" : "15px" }}
      >
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          onClick={() => setIsFilterCollapsed((prev) => !prev)}
          role="button"
          tabIndex={0}
          aria-expanded={!isFilterCollapsed}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsFilterCollapsed((prev) => !prev);
            }
          }}
          style={{ marginBottom: "14px", cursor: "pointer" }}
        >
          <FilterListIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter Service Requests</span>
          {isFilterCollapsed ? (
            <ExpandMoreIcon
              fontSize="small"
              className="text-muted"
              style={{
                width: "18px",
                height: "18px",
                flexShrink: 0,
                marginLeft: "auto",
              }}
            />
          ) : (
            <ExpandLessIcon
              fontSize="small"
              className="text-muted"
              style={{
                width: "18px",
                height: "18px",
                flexShrink: 0,
                marginLeft: "auto",
              }}
            />
          )}
        </div>

        {!isFilterCollapsed && (
        <div className="container-fluid">
          <div className="row g-3">
            {/* Business Vertical Dropdown */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Business Vertical
              </label>
              <CustomMultiSelect
                options={bvOptions}
                value={bvFilter}
                onChange={setBvFilterSafe}
                placeholder="Select Business Vertical"
              />
            </div>

            {/* Status Dropdown (Multiselect) */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <CustomMultiSelect
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilterSafe}
                placeholder="Select Status"
              />
            </div>

            {/* Date Range Picker */}
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">Select Date Range</label>
              <div
                className="input-icon filter-daterange-wrapper filter-daterangepickerWrpr"
                style={{ position: "relative" }}
              >
                <input
                  type="text"
                  ref={dateRangePickerRef}
                  className="form-control form-control-light text-start filter-daterangepicker"
                  placeholder="Select date range"
                  readOnly
                  style={{ height: "42px", textAlign: "left" }}
                />
                <CalendarTodayIcon
                  className="icon-right"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    width: "16px",
                    height: "16px",
                    color: "#6c757d",
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="col-12 col-md-6 col-lg-3">
              <label
                className="input-label d-block"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons align-items-center" style={{ position: "relative", zIndex: 10000 }}>
                <button
                  type="button"
                  className="btn btn-filter btn-sm"
                  disabled={loading}
                  onClick={() => {
                    // Wait a tick so checkbox selection state updates are applied
                    console.log("Filter button clicked");
                    setTimeout(() => handleFilterAndRefresh(), 0);
                  }}
                  style={{
                    position: "relative",
                    zIndex: 2147483647,
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "4px 12px",
                    fontSize: "0.875rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FilterListIcon fontSize="small" className="me-1" />
                      Filter
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter btn-sm"
                  onClick={() => {
                    setTimeout(() => handleReset(), 0);
                  }}
                  title="Reset"
                  style={{
                    position: "relative",
                    zIndex: 2147483647,
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "4px 10px",
                    fontSize: "0.875rem",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(132, 250, 176, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(132, 250, 176, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          {/* <div className="section-heading">SR List</div> */}
          <div className="table-responsive">
            <table
              ref={srTableRef}
              className="table table-striped table-bordered complaint-table custmTblLst"
              style={{ width: "100%" }}
            ></table>
          </div>
        </div>
      </Paper>
    </Box>
  );
};

export default Complaints;
